import type { MbcBriefAssetTarget } from "@nlc/shared-types";

import { useMemo } from "react";

import { Button } from "@/components/button";
import { toast } from "@/components/toast/toast-queue";

import type { UploadPromptData } from "../../types/mbc-chat.types";
import type { AssetGroupChange } from "../upload-assets/types";
import type { MbcUploadBinding } from "./UploadPromptPart";

import { patchBrief } from "../../lib/brief-api";
import { requestIdFromSessionId } from "../../lib/session-ids";
import { AssetUploadBlock } from "../upload-assets/AssetUploadBlock";
import { groupCount, readGroup } from "../upload-assets/types";

type AlphaUploadPromptPartProps = {
  isDisabled: boolean;
  onSelectChoice: (label: string) => void;
  prompt: UploadPromptData;
  /** "Done!" / "Skip" answer the agent's current question, so only the latest block shows them. */
  showActions: boolean;
  upload?: MbcUploadBinding;
};

const TITLES: Record<MbcBriefAssetTarget, string> = {
  assets: "Assets",
  inspirations: "Inspirations",
};

/**
 * The in-chat upload block in Alpha: bound to the brief's live group instead of
 * session state, so older blocks keep showing (and editing) the current files.
 */
export function AlphaUploadPromptPart({
  isDisabled,
  onSelectChoice,
  prompt,
  showActions,
  upload,
}: AlphaUploadPromptPartProps) {
  const { target } = prompt;
  const group = useMemo(
    () => readGroup(upload?.memory?.[target]),
    [target, upload?.memory],
  );
  const requestId = upload?.threadId
    ? requestIdFromSessionId(upload.threadId)
    : null;

  if (!upload || requestId === null) return null;

  const handleChange = (change: AssetGroupChange) => {
    patchBrief(upload.threadId!, upload.resourceId, {
      assetChanges: [{ target, ...change }],
    })
      .then(upload.onUploaded)
      .catch(() => {
        toast.add({
          description: "It wasn't saved to your brief. Please try again.",
          title: "Couldn't add that to the brief",
          type: "error",
        });
      });
  };

  return (
    <div className="mt-md flex flex-col gap-lg">
      <AssetUploadBlock
        onChange={handleChange}
        onFilesChanged={upload.onFilesChanged}
        onUploadingChange={upload.onUploadingChange}
        requestId={requestId}
        target={target}
        title={TITLES[target]}
        value={group}
      />
      {showActions ? (
        <div className="flex gap-lg">
          <Button
            isDisabled={isDisabled || groupCount(group) === 0}
            onPress={() => onSelectChoice("Done!")}
            variant="primary"
          >
            Done!
          </Button>
          <Button
            isDisabled={isDisabled}
            onPress={() => onSelectChoice("Skip this step")}
            variant="secondary"
          >
            Skip this step
          </Button>
        </div>
      ) : null}
    </div>
  );
}
